import { ref } from 'vue';

export interface RentalFilters {
  regionId: number | null
  unitConfigurations: string[]
  availabilityDates: string[] | undefined
  dogAllowed: boolean | undefined
  floors: number[] | undefined
  minPrice: number | undefined
  maxPrice: number | undefined
}

const STORAGE_KEY = 'rental-filters';

// Fonction utilitaire pour calculer le nombre de filtres secondaires
const calculateSecondaryFiltersCount = (filters: RentalFilters): number => {
  let count = 0;
  if (filters.floors && filters.floors.length > 0) {
    count++;
  }
  if (filters.minPrice !== undefined || filters.maxPrice !== undefined) {
    count++;
  }
  return count;
};

// Singleton state - shared across all component instances  
const countSecondaryRentalFilters = ref(0);

export function useRentalFilters() {
  const loadFiltersFromRentalStorage = (): RentalFilters => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Mettre à jour le compteur de filtres secondaires
        countSecondaryRentalFilters.value = calculateSecondaryFiltersCount(parsed);
        return {
          regionId: parsed.regionId || null,
          unitConfigurations: Array.isArray(parsed.unitConfiguration) ? parsed.unitConfiguration : [],
          availabilityDates: Array.isArray(parsed.availabilityDates) ? parsed.availabilityDates : undefined,
          dogAllowed: parsed.dogAllowed !== undefined ? parsed.dogAllowed : undefined,
          floors: Array.isArray(parsed.floors) ? parsed.floors : undefined,
          minPrice: parsed.priceRange?.min || undefined,
          maxPrice: parsed.priceRange?.max || undefined
        };
      }
    } catch (error) {
      console.warn('Unable to load filters from localStorage:', error);
    }
    return {
      regionId: null,
      unitConfigurations: [],
      availabilityDates: undefined,
      dogAllowed: undefined,
      floors: undefined,
      minPrice: undefined,
      maxPrice: undefined
    };
  };

  const saveFiltersToRentalStorage = (filters: Partial<RentalFilters>) => {
    try {
      const currentFilters = loadFiltersFromRentalStorage();
      const updatedFilters = { ...currentFilters, ...filters };
      
      const storageFormat = {
        regionId: updatedFilters.regionId,
        unitConfiguration: updatedFilters.unitConfigurations,
        availabilityDates: updatedFilters.availabilityDates,
        dogAllowed: updatedFilters.dogAllowed,
        floors: updatedFilters.floors,
        priceRange: {
          min: updatedFilters.minPrice,
          max: updatedFilters.maxPrice
        }
      };
      
      localStorage.setItem(STORAGE_KEY, JSON.stringify(storageFormat));
      
      // Mettre à jour le compteur de filtres secondaires
      countSecondaryRentalFilters.value = calculateSecondaryFiltersCount(updatedFilters);
    } catch (error) {
      console.warn('Unable to save filters to localStorage:', error);
    }
  };

  return {
    loadFiltersFromRentalStorage,
    saveFiltersToRentalStorage,
    countSecondaryRentalFilters
  };
}
